import { ConvivaEvent, mappedEventDimensionType } from '@src/common/constant/enum';
import { cloneDeep } from 'lodash';

export const buildQueryPayload = (key, rule, mappingRules) => {
  // Base payload structure
  const basePayload = {
    fields: [
      key === mappedEventDimensionType.merged_event_tag_value
        ? 'eventTags::values()'
        : 'eventTags::keys()',
      '::app_total_user_events',
    ],
    filters: [[{ field: 'event_name', op: 'eq', value: ConvivaEvent.conviva_network_request }]],
    limit: 10000,
    sortBy: { field: ['::app_total_user_events'], order: 'desc' },
  };

  // EventTags payload
  const eventTagsPayload = { ...cloneDeep(basePayload) };

  // CustomTags payload with modified fields
  const customTagsPayload = {
    ...basePayload,
    fields: [
      key === mappedEventDimensionType.merged_event_tag_value
        ? 'customTags::values()'
        : 'customTags::keys()',
      '::app_total_user_events',
    ],
  };

  // Add filters from mapping rules if provided
  if (mappingRules && Array.isArray(mappingRules)) {
    const eventTagsFilters = convertMappingRulesToFilters(mappingRules, 'eventTags');
    const customTagsFilters = convertMappingRulesToFilters(mappingRules, 'customTags');

    eventTagsPayload.filters.push(...eventTagsFilters);
    customTagsPayload.filters.push(...customTagsFilters);
  }

  // Add specific filters based on rule conditions
  if (
    key === mappedEventDimensionType.merged_event_tag_value &&
    rule.tag_key.value[0] &&
    rule?.tag_value?.value?.length === 0
  ) {
    eventTagsPayload.filters.push([
      { field: 'eventTags::keys()', op: rule.tag_key.op, value: rule.tag_key.value[0] },
    ]);
    customTagsPayload.filters.push([
      { field: 'customTags::keys()', op: rule.tag_key.op, value: rule.tag_key.value[0] },
    ]);
  } else if (
    key === mappedEventDimensionType.merged_event_tag_key &&
    rule.tag_value.value[0] &&
    rule?.tag_key?.value?.length === 0
  ) {
    eventTagsPayload.filters.push([
      { field: 'eventTags::values()', op: rule.tag_value.op, value: rule.tag_value.value[0] },
    ]);
    customTagsPayload.filters.push([
      { field: 'customTags::values()', op: rule.tag_value.op, value: rule.tag_value.value[0] },
    ]);
  }
  return {
    eventTagsPayload,
    customTagsPayload,
  };
};

export const convertMappingRulesToFilters = (
  mappingRules: any[],
  tagType: string = 'eventTags',
): any[] => {
  if (!mappingRules || !Array.isArray(mappingRules)) {
    return [];
  }

  const filters: any[] = [];

  // Process each rule group
  mappingRules.forEach((ruleGroup) => {
    if (!Array.isArray(ruleGroup) || ruleGroup.length === 0) {
      return;
    }

    const groupFilters: any[] = [];

    ruleGroup.forEach((rule) => {
      if (!rule.tag_key || !rule.tag_value) {
        return;
      }
      const tagKeyValues = rule.tag_key.value || [];
      const tagValueValues = rule.tag_value.value || [];
      const operator = rule.tag_value.op || 'eq';

      // Skip this rule if tag_key array is empty
      if (tagKeyValues.length === 0) {
        return;
      }

      const tagKey = tagKeyValues[0]; // Use the first tag key

      // For empty/not empty operators, create filter without values
      if (operator === 'is empty' || operator === 'is not empty') {
        groupFilters.push({
          field: `${tagType}.${tagKey}`,
          op: operator,
          // No value needed for empty/not empty operators
        });
        return;
      }
      // Skip this rule if tag_value array is empty (except for empty/not empty operators)
      if (tagValueValues.length === 0) {
        return;
      }

      // If both tag_key and tag_value are present, create proper filters
      tagValueValues.forEach((tagValue) => {
        groupFilters.push({
          field: `${tagType}.${tagKey}`,
          op: operator,
          value: tagValue,
        });
      });
    });

    // Only add the group if it has filters
    if (groupFilters.length > 0) {
      filters.push(groupFilters);
    }
  });

  return filters;
};
